'use client'

import type { FieldValues } from '@/ui/form/Form.client'
import {
  Children,
  type ReactElement,
  type ReactNode,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
} from 'react'
import type { AnyActionProps } from '../drawer.types'

export type DrawerContextValue = {
  heading: ReactNode
  setHeading: (newValue: ReactNode) => void
  body: ReactNode
  setBody: (newValue: ReactNode) => void
  registerAction: <T extends FieldValues>(action: AnyActionProps<T>) => void
  unregisterAction: <T extends FieldValues>(action: AnyActionProps<T>) => void
  footer: ReactNode
  setFooter: (newValue: ReactNode) => void
  close: () => void
}

export const DrawerContext = createContext<DrawerContextValue | null>(null)

export function useDrawer() {
  const ctx = useContext(DrawerContext)

  if (!ctx) {
    throw new Error('Drawer compound components must be used within a Drawer')
  }

  return ctx
}

export function useCloseDrawer(handle: string) {
  return useCallback(() => {
    window.postMessage({ type: 'CLOSE_DRAWER', handle }, '*')
  }, [handle])
}

export function DrawerHeading({ children }: { children: ReactNode }) {
  const { setHeading } = useDrawer()

  useEffect(() => {
    setHeading(children)

    return () => setHeading(null)
  }, [setHeading, children])

  return null
}

export function DrawerBody({ children }: { children: ReactNode }) {
  const { setBody } = useDrawer()

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !children) {
      throw new Error('DrawerBody is required for drawer')
    }

    setBody(children)

    return () => setBody(null)
  }, [setBody, children])

  return null
}

export function DrawerAction<T extends FieldValues>(action: AnyActionProps<T>) {
  const { registerAction, unregisterAction } = useDrawer()

  useEffect(() => {
    registerAction(action)

    return () => unregisterAction(action)
  }, [registerAction, unregisterAction, action])

  return null
}

DrawerAction.displayName = 'DrawerAction'

export function isDrawerActionElement(
  child: unknown,
): child is ReactElement<AnyActionProps<FieldValues>> {
  return isValidElement(child) && child.type === DrawerAction
}

export function DrawerFooter({ children }: { children: ReactNode }) {
  const { setFooter } = useDrawer()
  const isActionOnlyFooter = Children.toArray(children).every(isDrawerActionElement)

  useEffect(() => {
    setFooter(isActionOnlyFooter ? null : children)

    return () => setFooter(null)
  }, [setFooter, children, isActionOnlyFooter])

  // Actions render null but must be mounted here so they register in time to wrap the form.
  if (isActionOnlyFooter) {
    return <>{children}</>
  }

  return null
}
